import React, { useState, useEffect, useRef, useCallback } from 'react';
import api, { API_URL } from './api';
import Dashboard, { Stats } from './Dashboard';
import Admin, { ModalFestival, ModalActionDestructrice, Sauvegardes } from './Admin';
import Login from './Login';
import {
  couleurs, espace, rayon, texte, bouton, badge, bandeau, pastille,
  appliquerTheme, themeEnregistre, THEME_CLAIR, THEME_SOMBRE,
} from './theme';
import logoJums from './logo-jums.png';
import { useMobile, useDefilementBloque, useEchappe } from './ecran';
import TableauxMobiles from './TableauxMobiles';

const WS_URL = API_URL.replace('http', 'ws');

// ─── SÉLECTION ET GESTION DES FESTIVALS ────────────────────
//
// Refonte du 2026-08-27. Cet écran ne servait qu'à choisir ; toute la
// gestion vivait dans un onglet de l'administration, atteint par un
// SECOND bouton « Changer » qui ressemblait à celui-ci mais menait
// ailleurs. Deux boutons identiques pour deux destinations : le genre
// d'ambiguïté qui se paie un soir de festival.
//
// Ici : tout ce qui concerne la LISTE des festivals — choisir, créer,
// modifier, réinitialiser, supprimer.
// Là-bas (onglet « Festival ») : uniquement celui qu'on administre.

function SelectionFestival({ onSelect, onDeconnexion }) {
  const [festivals, setFestivals] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [msg, setMsg]             = useState({ texte: '', type: '' });
  // 'festivals' | 'sauvegardes' — voir le commentaire de sf.vues
  const [vue, setVue]             = useState('festivals');
  // { mode: 'creation' } | { mode: 'modification', festival }
  const [formulaire, setFormulaire] = useState(null);
  // { action: 'reinitialisation' | 'suppression', festival }
  const [actionDestructrice, setActionDestructrice] = useState(null);

  const charger = () => api.get('/admin/festivals')
    .then(res => setFestivals(res.data))
    .catch(() => setMsg({ texte: 'Liste des festivals indisponible', type: 'erreur' }))
    .finally(() => setLoading(false));

  useEffect(() => { charger(); }, []);

  /* Volontairement, un festival créé n'est PAS sélectionné d'office :
     la page ne se dérobe pas sous le clic. Il apparaît dans la liste,
     à un clic. */
  const apresEnregistrement = (f, creation) => {
    setMsg({ texte: creation ? `Festival « ${f.nom} » créé` : 'Festival modifié', type: 'succes' });
    charger();
  };

  /* Rendre un festival actif — le geste qui bascule la production.

     Volontairement SÉPARÉ de l'ouverture : ouvrir un festival pour
     regarder ses chiffres n'a aucun effet, même en plein service.
     Sans cette séparation, vérifier une comptabilité un samedi soir
     basculerait tout le système sans prévenir. */
  const activer = async (f) => {
    setMsg({ texte: '', type: '' });
    try {
      const res = await api.post(`/admin/festivals/${f.id}/activer`);
      setMsg({ texte: res.data.message, type: 'succes' });
      charger();
    } catch (e) {
      setMsg({ texte: e.response?.data?.detail || 'Erreur', type: 'erreur' });
    }
  };

  const apresAction = (data, suppression) => {
    setMsg(data.sauvegarde
      ? { texte: `${data.message} — sauvegarde préalable : ${data.sauvegarde}`, type: 'succes' }
      : { texte: `${data.message} — ⚠ ATTENTION : la sauvegarde préalable a ÉCHOUÉ, rien n'a été conservé`,
          type: 'erreur' });
    charger();
  };

  return (
    <div style={sf.page}>
      {/* Une barre discrète plutôt qu'un logo flottant : elle rend la
          déconnexion possible depuis cet écran, ce qui ne l'était pas —
          une session admin restait ouverte tant qu'on n'avait pas
          d'abord choisi un festival. */}
      <header className="jums-nav" style={sf.barre}>
        <Marque hauteur={17} />
        <div style={{ display: 'flex', alignItems: 'center', gap: espace.sm }}>
          <BasculeTheme />
          <button style={sf.lienDiscret} onClick={onDeconnexion}>Déconnexion</button>
        </div>
      </header>

      <main className="jums-page" style={sf.centre}>
        <div style={sf.intro}>
          <h1 style={sf.titre}>
            {vue === 'festivals' ? 'Choisir un festival' : 'Historique des sauvegardes'}
          </h1>
          <p style={sf.sousTitre}>
            {vue === 'festivals'
              ? "Lecteurs, gobelets, ventes — tout ce qui suit appartient au festival ouvert ici."
              : "Les sauvegardes portent sur la base entière, tous festivals confondus. Elles vivent ici, et non dans l'administration d'un festival, pour rester accessibles même après en avoir supprimé un."}
          </p>
        </div>

        {/* Deux vues plutôt qu'un onglet dans l'administration.
            Une sauvegarde porte sur TOUTE la base, pas sur un festival :
            la ranger dans l'administration d'un festival était trompeur,
            et surtout inaccessible dès qu'on supprimait le dernier — y
            compris la sauvegarde prise juste avant cette suppression. */}
        <div style={sf.vues} role="tablist">
          {[['festivals', 'Festivals'], ['sauvegardes', 'Sauvegardes']].map(([id, libelle]) => (
            <button key={id} role="tab" aria-selected={vue === id}
              style={{ ...sf.vue, ...(vue === id ? sf.vueActive : {}) }}
              onClick={() => setVue(id)}>
              {libelle}
            </button>
          ))}
        </div>

        {vue === 'sauvegardes' && (
          <div style={sf.panneau}><Sauvegardes /></div>
        )}

        {vue === 'festivals' && msg.texte && (
          <p style={{ ...bandeau(msg.type === 'succes' ? 'succes' : 'danger'),
                      marginBottom: espace.lg, maxWidth: 760 }}
             role={msg.type === 'succes' ? 'status' : 'alert'}>{msg.texte}</p>
        )}

        {vue === 'festivals' && (loading ? <p style={sf.info}>Chargement…</p> : (
          <div className="jums-grille" style={sf.grille}>
            {festivals.map(f => (
              <article key={f.id} style={{ ...sf.fiche, ...(f.actif ? sf.ficheActive : {}) }}
                onMouseEnter={e => { if (!f.actif) e.currentTarget.style.borderColor = couleurs.accent;
                                     e.currentTarget.style.boxShadow = couleurs.ombreSurvol; }}
                onMouseLeave={e => { if (!f.actif) e.currentTarget.style.borderColor = couleurs.bord;
                                     e.currentTarget.style.boxShadow = 'none'; }}>
                {/* Toute la partie haute est UN bouton : c'est la cible
                    d'ouverture. Les actions vivent dans leur propre
                    rangée — un clic sur « Supprimer » ne doit jamais
                    pouvoir ouvrir le festival au passage. */}
                <button style={sf.ficheChoix} onClick={() => onSelect(f)}>
                  {/* Le festival actif est celui que le collecteur
                      alimente. Il doit se voir de loin : c'est la seule
                      information de cet écran qui a des conséquences
                      sur le terrain. */}
                  {f.actif
                    ? <span style={sf.pastilleActif}>● EN PRODUCTION</span>
                    : <span style={sf.pastilleInactif}>consultation</span>}
                  <span style={sf.ficheNom}>{f.nom}</span>
                  <span style={sf.ficheLieu}>{f.lieu}</span>
                  <span style={sf.ficheDates}>{f.date_debut} → {f.date_fin}</span>
                  <span style={sf.ficheOuvrir}>Ouvrir →</span>
                </button>
                {/* « … » signale que rien ne se produit au clic : une
                    fenêtre s'ouvre, qui réclame le mot de passe et le
                    nom du festival. */}
                <div style={sf.ficheActions}>
                  {!f.actif && (
                    <button style={{ ...sf.action, color: couleurs.accent, fontWeight: 600 }}
                      onClick={() => activer(f)}
                      title="Le collecteur alimentera ce festival">
                      Rendre actif
                    </button>
                  )}
                  <button style={sf.action}
                    onClick={() => setFormulaire({ mode: 'modification', festival: f })}>
                    Modifier
                  </button>
                  <button style={sf.action}
                    onClick={() => setActionDestructrice({ action: 'reinitialisation', festival: f })}>
                    Réinitialiser…
                  </button>
                  <button style={{ ...sf.action, color: couleurs.danger }}
                    onClick={() => setActionDestructrice({ action: 'suppression', festival: f })}>
                    Supprimer…
                  </button>
                </div>
              </article>
            ))}

            {/* Une tuile de création à la suite des autres, plutôt qu'un
                « + » de 30 pixels dans un coin : c'est le geste qu'on
                cherche quand la liste est vide, il doit être aussi
                visible que les festivals eux-mêmes. */}
            <button style={sf.ficheNouvelle} onClick={() => setFormulaire({ mode: 'creation' })}
              onMouseEnter={e => { e.currentTarget.style.borderColor = couleurs.accent;
                                   e.currentTarget.style.color = couleurs.accent; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = couleurs.bord;
                                   e.currentTarget.style.color = couleurs.encreDouce; }}>
              <span style={sf.plus}>＋</span>
              <span style={sf.nouvelleTexte}>Nouveau festival</span>
            </button>
          </div>
        ))}
      </main>

      {formulaire && (
        <ModalFestival
          festival={formulaire.festival || null}
          onClose={() => setFormulaire(null)}
          onEnregistre={apresEnregistrement} />
      )}

      {actionDestructrice && (
        <ModalActionDestructrice
          action={actionDestructrice.action}
          festival={actionDestructrice.festival}
          onClose={() => setActionDestructrice(null)}
          onFait={apresAction} />
      )}
    </div>
  );
}

/* Écran de choix — refonte du 2026-08-27.

   L'ancienne version enfermait la liste dans une carte étroite au
   milieu de l'écran. Avec trois actions par ligne, tout devenait serré,
   et le « ＋ » de trente pixels dans un coin était introuvable.

   Ici : une page, pas une boîte. Les festivals sont des fiches dans une
   grille qui respire, la création est une tuile à part entière, et le
   survol dit clairement ce qui est ouvrable. */
const sf = {
  page: {
    minHeight: '100vh', background: couleurs.fond,
    display: 'flex', flexDirection: 'column',
  },
  barre: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: `${espace.md}px ${espace.xl}px`,
    borderBottom: `1px solid ${couleurs.bord}`,
    background: couleurs.surface,
  },
  marque: { ...texte.etiquette, color: couleurs.accent },
  lienDiscret: {
    background: 'transparent', border: 'none', cursor: 'pointer',
    fontSize: 12.5, color: couleurs.encreDouce, padding: 4,
  },
  centre: {
    flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center',
    justifyContent: 'center', padding: `${espace.xl}px ${espace.lg}px 64px`,
  },
  intro: { textAlign: 'center', maxWidth: 560, marginBottom: espace.lg },
  // Bascule entre les deux vues. Discrète : ce n'est pas une navigation
  // principale, juste deux façons de regarder la même page.
  vues: {
    display: 'inline-flex', gap: 2, marginBottom: espace.xl,
    padding: 3, background: couleurs.surfaceDouce,
    border: `1px solid ${couleurs.bord}`, borderRadius: rayon.lg,
  },
  vue: {
    background: 'transparent', border: 'none', cursor: 'pointer',
    padding: '6px 16px', borderRadius: rayon.md,
    fontSize: 13, fontWeight: 500, color: couleurs.encreDouce,
  },
  vueActive: {
    background: couleurs.surface, color: couleurs.encre, fontWeight: 600,
    boxShadow: couleurs.ombre,
  },
  panneau: { width: '100%', maxWidth: 860 },
  titre: { ...texte.titre, margin: `0 0 ${espace.xs}px`, fontSize: 26 },
  sousTitre: { ...texte.aide, margin: 0 },
  info: { ...texte.aide, padding: espace.xl, margin: 0 },
  // `auto-fill` et non `auto-fit` : avec un seul festival, la fiche
  // garde sa largeur au lieu de s'étirer sur toute la page.
  grille: {
    display: 'grid', gap: espace.lg, width: '100%', maxWidth: 860,
    gridTemplateColumns: 'repeat(auto-fill, minmax(238px, 1fr))',
    justifyContent: 'center',
  },
  fiche: {
    background: couleurs.surface,
    border: `1px solid ${couleurs.bord}`, borderRadius: rayon.lg,
    overflow: 'hidden', display: 'flex', flexDirection: 'column',
    transition: 'border-color 140ms ease, box-shadow 140ms ease',
  },
  // Le festival en production porte une bordure épaisse et colorée :
  // c'est la seule information de cet écran qui a des conséquences sur
  // le terrain, elle doit se repérer sans lire.
  ficheActive: {
    border: `2px solid ${couleurs.succes}`,
    background: couleurs.succesDoux,
  },
  pastilleActif: {
    fontSize: 10.5, fontWeight: 700, letterSpacing: '.06em',
    color: couleurs.succes, marginBottom: 6,
  },
  pastilleInactif: {
    fontSize: 10.5, fontWeight: 600, letterSpacing: '.06em',
    textTransform: 'uppercase', color: couleurs.encreFaible, marginBottom: 6,
  },
  ficheChoix: {
    flex: 1, width: '100%', padding: `${espace.lg}px`,
    background: 'transparent', border: 'none', cursor: 'pointer',
    textAlign: 'left', display: 'flex', flexDirection: 'column', gap: 2,
  },
  ficheNom: { fontSize: 16, fontWeight: 650, color: couleurs.encre, lineHeight: 1.3 },
  ficheLieu: { fontSize: 13, color: couleurs.encreDouce, marginTop: 2 },
  ficheDates: {
    fontSize: 12, color: couleurs.encreFaible,
    fontVariantNumeric: 'tabular-nums', marginTop: 1,
  },
  ficheOuvrir: {
    ...texte.etiquette, color: couleurs.accent,
    marginTop: espace.md, fontSize: 11,
  },
  ficheActions: {
    display: 'flex', gap: espace.sm, flexWrap: 'wrap',
    padding: `8px ${espace.lg}px 10px`,
    borderTop: `1px solid ${couleurs.bord}`,
  },
  action: {
    background: 'transparent', border: 'none', padding: 0,
    fontSize: 11.5, color: couleurs.encreDouce, cursor: 'pointer',
    textDecoration: 'underline', textUnderlineOffset: 2,
  },
  // Bordure en pointillés : le vocabulaire universel du « rien ici
  // encore, cliquez pour en mettre ».
  ficheNouvelle: {
    minHeight: 148, background: 'transparent',
    border: `1px dashed ${couleurs.bord}`, borderRadius: rayon.lg,
    cursor: 'pointer', color: couleurs.encreDouce,
    display: 'flex', flexDirection: 'column', alignItems: 'center',
    justifyContent: 'center', gap: espace.xs,
    transition: 'border-color 140ms ease, color 140ms ease',
  },
  plus: { fontSize: 22, lineHeight: 1 },
  nouvelleTexte: { fontSize: 13, fontWeight: 500 },
};


// ─── INTERFACE BARMAN ──────────────────────────────────────

function BarmanInterface({ tapisId }) {
  const [commande, setCommande]     = useState(null);
  const [verresUids, setVerresUids] = useState([]);
  const [connecte, setConnecte]     = useState(false);
  const [validating, setValidating] = useState(false);
  const [message, setMessage]       = useState('');
  const [erreur, setErreur]         = useState('');
  const [scanErreurs, setScanErreurs] = useState({});
  const [verreRendu, setVerreRendu]   = useState(null);
  const wsRef = useRef(null);
  // Miroir de la commande courante pour le handler WS (la closure du
  // useEffect capture un état figé — sans ref, impossible de comparer
  // l'ordre_id d'un message à la commande réellement affichée).
  const commandeRef = useRef(null);
  useEffect(() => { commandeRef.current = commande; }, [commande]);

  useEffect(() => {
    if (!tapisId) return;
    if (wsRef.current) { wsRef.current.onclose = null; wsRef.current.close(); }

    const token = localStorage.getItem('token');
    const ws = new WebSocket(`${WS_URL}/ws/tapis/${tapisId}?token=${token}`);

    ws.onopen  = () => { setConnecte(true); console.log('WS barman connecté sur tapis', tapisId); };
    ws.onclose = () => setConnecte(false);
    ws.onerror = () => ws.close();
    ws.onmessage = (event) => {
      const data = JSON.parse(event.data);
      if (data.type === 'nouvelle_commande' || data.popup_active === true) {
        const ordreId = data.ordre_id || (data.ordres && data.ordres[0]?.ordre_id);
        setCommande({ ...data, ordre_id: ordreId });
        setVerresUids(data.verres_uids || []);
        setMessage(''); setErreur(''); setScanErreurs({});
      } else if (data.type === 'progression') {
        setVerresUids(data.verres_uids || []);
        setCommande(prev => prev ? { ...prev, ...data } : { ...data, type: 'nouvelle_commande' });
        setErreur('');
      } else if (data.type === 'commande_validee' || data.type === 'commande_expiree') {
        // La commande peut se terminer AILLEURS que sur la tablette :
        // validation ou scan-oublié depuis le widget du terminal, ou
        // expiration automatique par le moteur d'échéance (rev14).
        // Sans ce traitement, la popup restait ouverte sur un état
        // périmé et toutes ses actions renvoyaient des erreurs.
        const actuelle = commandeRef.current;
        if (!actuelle || !data.ordre_id || actuelle.ordre_id === data.ordre_id) {
          setCommande(null); setVerresUids([]); setErreur(''); setScanErreurs({});
          setMessage(data.type === 'commande_expiree'
            ? '⏱ Temps écoulé — consignes non validées remboursées automatiquement'
            : (data.scan_oublie
                ? '↩️ Commande clôturée depuis le widget — consignes non scannées remboursées'
                : '✅ Commande validée depuis le widget'));
          setTimeout(() => setMessage(''), 4000);
        }
      } else if (data.type === 'erreur_scan') {
        setErreur(data.message || 'Erreur de scan');
      } else if (data.type === 'verre_rendu') {
        setVerreRendu({ rfid: data.rfid_uid, remboursement: data.remboursement });
        setTimeout(() => setVerreRendu(null), 3000);
      } else if (data.type === 'reinitialisation') {
        setCommande(null); setVerresUids([]); setMessage(''); setErreur(''); setScanErreurs({});
      }
    };
    wsRef.current = ws;
    return () => { ws.onclose = null; ws.close(); };
  }, [tapisId]);

  const retirerVerre = async (rfid) => {
    if (!commande) return;
    try {
      await api.post('/verres/retirer-verre', { rfid_uid_verre: rfid, ordre_id: commande.ordre_id });
    } catch (e) { setErreur(e.response?.data?.detail || 'Erreur'); }
  };

  const valider = async () => {
    if (!commande) return;
    setValidating(true); setErreur('');
    try {
      const res = await api.post('/verres/valider-ordre', { ordre_id: commande.ordre_id });
      setMessage(`✅ ${res.data.total} verre(s) remis au client !`);
      setTimeout(() => { setCommande(null); setVerresUids([]); setMessage(''); }, 2500);
    } catch (e) { setErreur(e.response?.data?.detail || 'Erreur lors de la validation'); }
    setValidating(false);
  };

  const annuler = async () => {
    if (!commande) return;
    try {
      await api.post(`/popup/ordre/${commande.ordre_id}/annuler`);
      setCommande(null); setVerresUids([]); setErreur(''); setMessage('');
    } catch (e) { setErreur(e.response?.data?.detail || 'Erreur annulation'); }
  };
  const effacerScans = async () => {
    if (!commande) return;
    try {
      await api.post('/verres/effacer-scans', { ordre_id: commande.ordre_id });
      setErreur(''); setMessage('');
    } catch (e) { setErreur(e.response?.data?.detail || 'Erreur'); }
  };

  const scanOublie = async () => {
    if (!commande) return;
    if (!window.confirm(
      nbScannes > 0
        ? `Garder ${nbScannes} verre(s) scanné(s) associé(s) au client et rembourser ` +
          `immédiatement les ${totalVerres - nbScannes} consigne(s) non scannée(s) ?\n\n` +
          `À utiliser si une partie des verres a été servie sans être scannée et que le client est parti.`
        : `Rembourser immédiatement les ${totalVerres} consigne(s) de cette commande ?\n\n` +
          `À utiliser si les verres ont été servis sans être scannés et que le client est parti. ` +
          `La commande sera annulée et le client remboursé.`
    )) return;
    try {
      await api.post('/verres/scan-oublie', { ordre_id: commande.ordre_id });
      setCommande(null); setVerresUids([]); setErreur(''); setMessage('');
    } catch (e) { setErreur(e.response?.data?.detail || 'Erreur'); }
  };

  const totalVerres  = commande?.total_verres || 0;
  const nbScannes    = verresUids.length;
  const pretAValider = nbScannes >= totalVerres && totalVerres > 0;

  if (!tapisId) {
    return (
      <div style={styles.container}>
        <h2 style={styles.titre}>Interface barman</h2>
        <p style={{ color: '#FF8A80', fontSize: 14, maxWidth: 380, textAlign: 'center' }}>
          Aucun tapis associé à ce compte — le scan est impossible.
          Contactez l'administrateur.
        </p>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <h2 style={styles.titre}>Interface barman</h2>
      <p style={styles.attente}>En attente d'une commande…</p>
      {/* État de la liaison : un point, puis du texte. Le point seul
          (« ● ») obligeait à connaître le code couleur ; le mot le dit. */}
      <p style={{ fontSize: 12, color: '#9AA1AC', display: 'flex',
                  alignItems: 'center', gap: 7, marginTop: espace.sm }}>
        <span style={{ ...pastille(connecte ? 'succes' : 'danger'),
                       background: connecte ? '#3FB950' : '#F85149' }} />
        {connecte ? `Connecté — tapis ${tapisId}` : 'Reconnexion en cours…'}
      </p>

      {verreRendu && (
        <div style={{
          position: 'fixed', bottom: 28, left: '50%', transform: 'translateX(-50%)',
          background: couleurs.surface, color: couleurs.encre,
          border: `1px solid ${couleurs.bord}`, borderLeft: `2px solid ${couleurs.succes}`,
          borderRadius: rayon.lg, padding: '12px 18px',
          display: 'flex', alignItems: 'center', gap: 10, zIndex: 3000, fontSize: 13.5,
        }} role="status">
          <span style={{ ...pastille('succes') }} />
          Gobelet rendu — consigne remboursée
        </div>
      )}

      {commande && (
        <div className="jums-modale-voile" style={styles.overlay}>
          <div className="jums-modale" style={styles.popup}>
            <h2 style={styles.popupTitre}>Nouvelle commande</h2>
            <p style={styles.popupMessage}>
              {totalVerres} gobelet{totalVerres > 1 ? 's' : ''} à poser sur le lecteur
            </p>

            <div style={styles.progression}>
              {Array.from({ length: totalVerres }).map((_, i) => (
                <div key={i} style={{
                  ...styles.cercle,
                  background: i < nbScannes ? couleurs.accent : couleurs.surfaceDouce,
                  color: i < nbScannes ? '#FFFFFF' : couleurs.encreFaible,
                }}>
                  {i + 1}
                </div>
              ))}
            </div>

            <p style={styles.compteur}>{nbScannes} sur {totalVerres} détecté{nbScannes > 1 ? 's' : ''}</p>

            {verresUids.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5,
                            justifyContent: 'center', marginBottom: espace.lg }}>
                {verresUids.map((uid, i) => (
                  <div key={i} style={{
                    display: 'flex', alignItems: 'center', gap: 4,
                    background: couleurs.surfaceDouce, border: `1px solid ${couleurs.bord}`,
                    borderRadius: rayon.sm, padding: '2px 3px 2px 7px',
                  }}>
                    <span style={{ fontFamily: 'ui-monospace, Menlo, Consolas, monospace',
                                   fontSize: 11, color: couleurs.encreDouce }}>{uid}</span>
                    <button
                      style={{ width: 17, height: 17, borderRadius: rayon.sm,
                               background: 'transparent', border: 'none', cursor: 'pointer',
                               color: couleurs.encreFaible, fontSize: 11, lineHeight: 1,
                               display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                      onClick={() => retirerVerre(uid)}
                      title={`Retirer le gobelet ${uid}`}
                      aria-label={`Retirer le gobelet ${uid}`}
                    >✕</button>
                  </div>
                ))}
              </div>
            )}

            {erreur  && <p style={{ ...badge('danger'), marginBottom: espace.sm }} role="alert">{erreur}</p>}
            {message && <p style={{ ...badge('succes'), marginBottom: espace.sm }} role="status">{message}</p>}

            <button
              style={{ ...styles.btnOk, opacity: pretAValider && !validating ? 1 : 0.45,
                       cursor: pretAValider && !validating ? 'pointer' : 'not-allowed' }}
              onClick={valider}
              disabled={!pretAValider || validating}
            >
              {validating ? 'Validation…'
                : pretAValider ? 'Valider — remettre les gobelets au client'
                : `Il manque ${totalVerres - nbScannes} gobelet${totalVerres - nbScannes > 1 ? 's' : ''}`}
            </button>
            <button style={styles.btnSecondaire} onClick={effacerScans}>
              Effacer et rescanner
            </button>
            <button style={styles.btnSecondaire} onClick={scanOublie}>
              Rembourser les consignes immédiatement
            </button>
            <button style={styles.btnAnnuler} onClick={annuler}>
              Annuler la commande
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/* L'interface barman garde un fond SOMBRE, à contre-courant du reste
   de l'outil — et c'est délibéré. Elle vit en plein écran sur une
   tablette posée au bar, souvent la nuit, et sert à une seule chose :
   voir de loin combien de gobelets sont scannés. Un fond clair y
   éblouirait, et les pastilles de progression y perdraient leur
   contraste. Les écrans de PILOTAGE (dashboard, admin) sont clairs ;
   l'écran de TERRAIN reste sombre. */
const styles = {
  container: {
    minHeight: '100vh', background: '#101216',
    display: 'flex', flexDirection: 'column', alignItems: 'center',
    justifyContent: 'center', color: '#F2F3F5', paddingTop: 60,
  },
  titre: { fontSize: 24, fontWeight: 600, letterSpacing: '-0.015em', marginBottom: espace.sm },
  attente: { color: '#9AA1AC', fontSize: 15 },
  overlay: {
    position: 'fixed', inset: 0, background: 'rgba(8,9,11,0.72)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 2000, padding: espace.lg,
  },
  popup: {
    background: couleurs.surface, borderRadius: rayon.xl, padding: 32,
    width: 480, maxWidth: '100%', textAlign: 'center',
    color: couleurs.encre, maxHeight: '90vh', overflowY: 'auto',
  },
  popupTitre: { ...texte.titre, marginBottom: espace.xs },
  popupMessage: { fontSize: 14, marginBottom: espace.lg, color: couleurs.encreDouce },
  compteur: { ...texte.etiquette, marginBottom: espace.lg },
  progression: {
    display: 'flex', justifyContent: 'center', gap: espace.sm,
    marginBottom: espace.md, flexWrap: 'wrap',
  },
  // Pastilles de comptage : la seule chose qu'on regarde à distance.
  cercle: {
    width: 44, height: 44, borderRadius: rayon.rond,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: 15, fontWeight: 600, fontVariantNumeric: 'tabular-nums',
    transition: 'background 200ms ease, color 200ms ease',
  },
  btnOk: { ...bouton('primaire'), width: '100%', padding: 13, fontSize: 14.5,
           fontWeight: 600, marginBottom: espace.sm },
  btnSecondaire: { ...bouton('discret'), width: '100%', marginBottom: espace.sm },
  btnAnnuler: { ...bouton('danger'), width: '100%' },
};


// ─── NAVIGATION ────────────────────────────────────────────
// Barre claire, séparée du contenu par une simple bordure. L'onglet
// actif se signale par un trait sous le libellé plutôt que par une
// pastille colorée : moins de bruit, et la position reste lisible même
// quand on lit la page en diagonale.

const navStyles = {
  nav: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    gap: espace.lg, padding: '0 24px', height: 52,
    background: couleurs.surface,
    borderBottom: `1px solid ${couleurs.bord}`,
    position: 'fixed', top: 0, left: 0, right: 0, zIndex: 1000,
  },
  gauche: { display: 'flex', gap: espace.xs, alignItems: 'center', minWidth: 0 },
  /* Le logo remplace le mot « JUMS » depuis le 01/09/2026 : plus de
     petites capitales, donc plus de réglages typographiques ici — juste
     l'espace qui sépare la marque du premier onglet. */
  marque: { display: 'flex', alignItems: 'center', marginRight: espace.lg, flex: 'none' },
  btn: {
    padding: '0 12px', height: 52, border: 'none', background: 'transparent',
    cursor: 'pointer', fontSize: 13.5, color: couleurs.encreDouce,
    borderBottom: '2px solid transparent', marginBottom: -1,
  },
  actif: { color: couleurs.encre, fontWeight: 600, borderBottomColor: couleurs.accent },
  droite: { display: 'flex', gap: espace.md, alignItems: 'center', flex: 'none' },
  badge: {
    fontSize: 12.5, color: couleurs.encreDouce,
    maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
  },
  deconnexion: { ...bouton('discret', 'sm') },
};


/* ── Navigation sur écran étroit (03/09/2026) ──────────────────────
   Sur ordinateur, la barre affiche les destinations les unes à côté
   des autres. Sur un téléphone de 360 px, « Vue d'ensemble »,
   « Administration », « Statistiques », le nom du festival, « Changer »
   et « Déconnexion » ne tiennent pas : ils débordaient ou se
   chevauchaient.

   Le tiroir ne montre RIEN de neuf — exactement les mêmes destinations,
   dans le même ordre, avec les mêmes mots. Seule la disposition change :
   empilées au lieu d'alignées. C'est la même application, sur un écran
   plus étroit.

   Rien de ceci n'existe au-dessus de 760 px : le composant n'est même
   pas monté. */

function IconeBurger({ ouvert }) {
  /* Dessiné en SVG plutôt qu'importé : il hérite ainsi de la couleur du
     thème, en clair comme en sombre, sans deux fichiers à maintenir.
     La croix quand le tiroir est ouvert : le bouton dit toujours ce
     qu'il va faire. */
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
         stroke="currentColor" strokeWidth="1.9" strokeLinecap="round"
         aria-hidden="true">
      {ouvert
        ? <path d="M6 6l12 12M18 6L6 18" />
        : <path d="M3.5 7h17M3.5 12h17M3.5 17h17" />}
    </svg>
  );
}

function BoutonBurger({ ouvert, onClick }) {
  return (
    <button className="jums-burger jums-icone" onClick={onClick}
      aria-label={ouvert ? 'Fermer le menu' : 'Ouvrir le menu'}
      aria-expanded={ouvert}
      style={{
        alignItems: 'center', justifyContent: 'center',
        background: 'transparent', border: 'none', cursor: 'pointer',
        color: couleurs.encre, padding: 6, lineHeight: 0,
        borderRadius: rayon.md, flex: 'none', marginRight: espace.xs,
      }}>
      <IconeBurger ouvert={ouvert} />
    </button>
  );
}

/* Le tiroir lui-même. `destinations` est la liste déjà construite par
   la barre : on ne redéclare pas les écrans à deux endroits, sous peine
   d'en oublier un le jour où on en ajoute. */
function TiroirNavigation({ ouvert, onFermer, destinations, vue, setVue,
                            festival, role, onChangerFestival, onDeconnexion }) {
  useDefilementBloque(ouvert);
  useEchappe(ouvert, onFermer);
  if (!ouvert) return null;

  const aller = (id) => { setVue(id); onFermer(); };

  return (
    <>
      <div className="jums-voile-tiroir" onClick={onFermer} aria-hidden="true" />
      <nav className="jums-tiroir" aria-label="Menu principal">
        <div style={tiroirStyles.entete}>
          <Marque hauteur={17} />
          <button onClick={onFermer} aria-label="Fermer le menu"
            className="jums-icone" style={tiroirStyles.fermer}>
            <IconeBurger ouvert />
          </button>
        </div>

        {/* Le festival ouvert, rappelé en tête : sur téléphone il ne
            tient pas dans la barre, et c'est pourtant le contexte de
            tout ce qu'on va lire ensuite. */}
        {festival && (
          <div style={tiroirStyles.contexte}>
            <span style={tiroirStyles.contexteEtiquette}>Festival ouvert</span>
            <span style={tiroirStyles.contexteNom}>{festival.nom}</span>
          </div>
        )}

        <div style={tiroirStyles.liste}>
          {destinations.map(([id, libelle]) => (
            <button key={id} onClick={() => aller(id)}
              aria-current={vue === id ? 'page' : undefined}
              style={{ ...tiroirStyles.lien, ...(vue === id ? tiroirStyles.lienActif : {}) }}>
              {libelle}
            </button>
          ))}
        </div>

        <div style={tiroirStyles.pied}>
          {festival && role === 'admin' && (
            <button style={tiroirStyles.action}
              onClick={() => { onChangerFestival(null); onFermer(); }}>
              Changer de festival
            </button>
          )}
          <div style={tiroirStyles.pledRangee}>
            <BasculeTheme />
            <button style={{ ...bouton('discret', 'sm'), flex: 1 }}
              onClick={() => { onDeconnexion(); onFermer(); }}>
              Déconnexion
            </button>
          </div>
        </div>
      </nav>
    </>
  );
}

/* Les styles du tiroir empruntent tout au thème : mêmes couleurs,
   mêmes rayons, même échelle d'espacement que le reste de la console.
   Rien n'est inventé ici. */
const tiroirStyles = {
  entete: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: `0 ${espace.md}px`, height: 52,
    borderBottom: `1px solid ${couleurs.bord}`, flex: 'none',
  },
  fermer: {
    background: 'transparent', border: 'none', cursor: 'pointer',
    color: couleurs.encreDouce, padding: 6, lineHeight: 0,
    borderRadius: rayon.md, display: 'inline-flex', alignItems: 'center',
    justifyContent: 'center',
  },
  contexte: {
    display: 'flex', flexDirection: 'column', gap: 2,
    padding: `${espace.md}px ${espace.lg}px`,
    borderBottom: `1px solid ${couleurs.bord}`,
  },
  contexteEtiquette: { ...texte.etiquette },
  contexteNom: { ...texte.corps, fontWeight: 600 },
  liste: { display: 'flex', flexDirection: 'column', padding: `${espace.sm}px 0`, flex: 1 },
  lien: {
    textAlign: 'left', background: 'transparent', border: 'none',
    borderLeft: '3px solid transparent', cursor: 'pointer',
    padding: `13px ${espace.lg}px`, fontSize: 15,
    fontFamily: 'inherit', color: couleurs.encreDouce,
  },
  lienActif: {
    color: couleurs.encre, fontWeight: 600,
    borderLeftColor: couleurs.accent, background: couleurs.surfaceDouce,
  },
  pied: {
    display: 'flex', flexDirection: 'column', gap: espace.sm,
    padding: espace.lg, borderTop: `1px solid ${couleurs.bord}`, flex: 'none',
  },
  action: { ...bouton('discret', 'md'), width: '100%' },
  pledRangee: { display: 'flex', alignItems: 'center', gap: espace.sm },
};


// ─── APP PRINCIPALE ────────────────────────────────────────

function AppConnecte() {
  const [vue, setVue]         = useState('dashboard');
  const [connecte, setConnecte] = useState(false);
  const [role, setRole]         = useState(null);
  const [festival, setFestival] = useState(null);
  const [tapisId, setTapisId]   = useState(null);
  /* Écran étroit : la barre se replie derrière un bouton burger.
     Déclaré ICI, avec les autres états, et non plus bas : un hook doit
     être appelé avant les retours anticipés (`if (!connecte) …`),
     sinon React refuse le composant. */
  const mobile                  = useMobile();
  const [menuOuvert, setMenuOuvert] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      setConnecte(true);
      const r = localStorage.getItem('role');
      setRole(r);
      // Vue par défaut selon le rôle
      setVue(r === 'admin' ? 'dashboard' : 'barman');
      const f = localStorage.getItem('festival');
      if (f) setFestival(JSON.parse(f));
      setTapisId(localStorage.getItem('tapis_id'));
    }
  }, []);

  const handleLogin = (data) => {
    setConnecte(true);
    setRole(data.role);
    setVue(data.role === 'admin' ? 'dashboard' : 'barman');
    if (data.tapis_id) {
      setTapisId(data.tapis_id);
      localStorage.setItem('tapis_id', data.tapis_id);
    }
  };

  const handleFestivalSelect = (f) => {
    setFestival(f);
    localStorage.setItem('festival', JSON.stringify(f));
  };

  /* Appelé par l'administration : le festival a été renommé, ou
     supprimé (`null`). Dans ce dernier cas, plus rien à administrer —
     on retombe sur l'écran de choix, où l'on peut en ouvrir un autre ou
     en créer un. Le vider du stockage local est indispensable : sans
     ça, le rechargement suivant restaurerait un festival qui n'existe
     plus, et tous les écrans interrogeraient un identifiant mort. */
  const handleFestivalChange = (f) => {
    setFestival(f);
    if (f) localStorage.setItem('festival', JSON.stringify(f));
    else localStorage.removeItem('festival');
  };

  const handleDeconnexion = () => {
    localStorage.clear();
    setConnecte(false); setRole(null); setFestival(null); setTapisId(null);
  };

  if (!connecte) return <Login onLogin={handleLogin} />;
  if (role === 'admin' && !festival) {
    return <SelectionFestival onSelect={handleFestivalSelect} onDeconnexion={handleDeconnexion} />;
  }

  /* La liste des destinations, écrite UNE fois : la barre large et le
     tiroir étroit la partagent. Sans ça, ajouter un écran demanderait
     de penser aux deux, et l'un des deux serait oublié. */
  const destinations = role === 'barman'
    ? [['barman', 'Interface barman']]
    : [['dashboard', "Vue d'ensemble"], ['admin', 'Administration'], ['stats', 'Statistiques']];

  return (
    <div>
      {/* Ne rend rien : recopie les en-têtes de colonne dans les
          cellules pour que les tableaux deviennent lisibles en fiches
          sur téléphone. Inerte sur ordinateur. */}
      <TableauxMobiles />

      {mobile ? (
        /* ── Barre repliée ──────────────────────────────────────
           Le burger, la marque, et le nom du festival s'il tient.
           Tout le reste — destinations, changement de festival,
           thème, déconnexion — vit dans le tiroir. */
        <nav className="jums-nav" style={navStyles.nav}>
          <div style={navStyles.gauche}>
            <BoutonBurger ouvert={menuOuvert} onClick={() => setMenuOuvert(o => !o)} />
            <span style={{ ...navStyles.marque, marginRight: 0 }}><Marque /></span>
          </div>
          <div style={navStyles.droite}>
            {festival && (
              <span style={{ ...navStyles.badge, maxWidth: 150 }} title={festival.nom}>
                {festival.nom}
              </span>
            )}
          </div>
        </nav>
      ) : (
      <nav style={navStyles.nav}>
        <div style={navStyles.gauche}>
          <span style={navStyles.marque}><Marque /></span>

          {/* ── Barman : uniquement l'interface barman ── */}
          {role === 'barman' && (
            <button style={{ ...navStyles.btn, ...(vue === 'barman' ? navStyles.actif : {}) }} onClick={() => setVue('barman')}>
              Interface barman
            </button>
          )}

          {/* ── Admin : vue d'ensemble, administration, statistiques ── */}
          {role === 'admin' && (
            <>
              <button style={{ ...navStyles.btn, ...(vue === 'dashboard' ? navStyles.actif : {}) }} onClick={() => setVue('dashboard')}>
                Vue d'ensemble
              </button>
              <button style={{ ...navStyles.btn, ...(vue === 'admin' ? navStyles.actif : {}) }} onClick={() => setVue('admin')}>
                Administration
              </button>
              <button style={{ ...navStyles.btn, ...(vue === 'stats' ? navStyles.actif : {}) }} onClick={() => setVue('stats')}>
                Statistiques
              </button>
            </>
          )}
        </div>

        {/* Le festival actif et la déconnexion sont poussés À DROITE :
            ce sont des informations de contexte, pas des destinations.
            Mélangés aux onglets, ils faisaient hésiter sur ce qui est
            cliquable pour naviguer. */}
        <div style={navStyles.droite}>
          {festival && (
            <>
              <span style={navStyles.badge} title={festival.nom}>{festival.nom}</span>
              {/* LE bouton « Changer » — le seul depuis le 2026-08-27.
                  Il mène à l'écran de choix, qui porte aussi la création,
                  la modification, la réinitialisation et la suppression. */}
              {role === 'admin' && (
                <button onClick={() => handleFestivalChange(null)}
                  style={{ ...bouton('discret', 'sm'), color: couleurs.encreDouce }}
                  title="Choisir, créer ou gérer les festivals">
                  Changer
                </button>
              )}
            </>
          )}
          <BasculeTheme />
          <button style={navStyles.deconnexion} onClick={handleDeconnexion}>Déconnexion</button>
        </div>
      </nav>
      )}

      {mobile && (
        <TiroirNavigation
          ouvert={menuOuvert} onFermer={() => setMenuOuvert(false)}
          destinations={destinations} vue={vue} setVue={setVue}
          festival={festival} role={role}
          onChangerFestival={handleFestivalChange}
          onDeconnexion={handleDeconnexion} />
      )}

      {vue === 'barman'    && <BarmanInterface tapisId={tapisId} />}
      {vue === 'dashboard' && role === 'admin' && <Dashboard festivalId={festival?.id} />}
      {vue === 'admin'     && role === 'admin' && <Admin festivalId={festival?.id} onFestivalChange={handleFestivalChange} />}
      {vue === 'stats'     && role === 'admin' && <Stats festivalId={festival?.id} />}
    </div>
  );
}


/* ── Marque et bascule de thème ────────────────────────────────────
   Le logo remplace le mot « JUMS » écrit en petites capitales, présent
   à deux endroits (écran de choix du festival, barre de navigation).
   Un composant partagé plutôt qu'une image collée deux fois : la
   hauteur et l'alignement restent forcément identiques.

   Le PNG est importé, donc empaqueté et versionné par le build : son
   nom de fichier change à chaque modification et aucun navigateur ne
   peut servir l'ancien depuis son cache. */
function Marque({ hauteur = 18 }) {
  return (
    <img src={logoJums} alt="JUMS"
         style={{ height: hauteur, width: 'auto', display: 'block', flex: 'none' }} />
  );
}

/* L'interrupteur clair / sombre. Il ne porte aucun texte : à côté de
   « Déconnexion » et du nom du festival, un mot de plus encombrerait
   une barre déjà dense. L'icône montre la DESTINATION — une lune quand
   on peut passer en sombre, un soleil quand on peut revenir — parce que
   c'est ce qu'on cherche du regard, pas l'état courant, qu'on a sous
   les yeux. */
function BasculeTheme() {
  const [theme, setTheme] = useState(themeEnregistre);
  const sombre = theme === THEME_SOMBRE;
  const cible = sombre ? THEME_CLAIR : THEME_SOMBRE;

  const basculer = () => {
    /* `true` : la transition douce n'a lieu QUE sur une bascule
       volontaire, jamais au chargement ni sur un rafraîchissement de
       tableau. */
    setTheme(appliquerTheme(cible, true));
  };

  return (
    <button onClick={basculer}
      title={sombre ? 'Passer en thème clair' : 'Passer en thème sombre'}
      aria-label={sombre ? 'Passer en thème clair' : 'Passer en thème sombre'}
      style={{
        background: 'transparent', border: 'none', cursor: 'pointer',
        color: couleurs.encreDouce, padding: 6, lineHeight: 0,
        borderRadius: rayon.md, flex: 'none',
      }}>
      <svg width="17" height="17" viewBox="0 0 24 24" fill="none"
           stroke="currentColor" strokeWidth="1.9"
           strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        {sombre ? (
          <>
            <circle cx="12" cy="12" r="4.2" />
            <path d="M12 2.5v2M12 19.5v2M2.5 12h2M19.5 12h2M5.1 5.1l1.4 1.4M17.5 17.5l1.4 1.4M18.9 5.1l-1.4 1.4M6.5 17.5l-1.4 1.4" />
          </>
        ) : (
          <path d="M20.5 14.6A8.6 8.6 0 1 1 9.4 3.5a6.9 6.9 0 0 0 11.1 11.1z" />
        )}
      </svg>
    </button>
  );
}


// ─── RACINE ────────────────────────────────────────────────
// Ménage du 2026-08-16 : la route publique /widget/:ordreId a été
// retirée avec le composant Widget qu'elle servait. C'était l'ancien
// widget React, ouvert par le partenaire via window.open(widget_url) —
// un flux supprimé du contrat en rev15. Le terminal charge désormais
// app/templates/consigne.html directement depuis l'API. Ce fichier
// mort avait déjà fait modifier des couleurs sans effet.
export default function App() {
  return <AppConnecte />;
}
